import { Clips } from '@air/api';
import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import invariant from 'tiny-invariant';

import { ONE_DAY } from '~/constants/unitsOfTime';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSearchTranscriptFetchQueueContext } from '~/providers/SearchTranscriptFetchQueueProvider';
import { MAX_TRANSCRIPT_RANGE_IN_MS } from '~/swr-hooks/clips/transcript/constants';
import { type SearchTranscriptCache } from '~/swr-hooks/clips/transcript/search/types';
import { type TimeRange } from '~/swr-hooks/clips/transcript/types';
import { getDataForTimeInterval } from '~/swr-hooks/clips/transcript/utils/getDataForTimeInterval';

export const getSearchPrivateTranscriptKey = (clipId: string, search: string) => [
  'SEARCH_PRIVATE_TRANSCRIPT',
  {
    clipId,
    search,
  },
];

export const MIN_TRANSCRIPT_QUERY_LENGTH = 2;

export const useSearchPrivateTranscript = ({
  search,
  clipId,
  videoDurationMs,
  enabled,
}: {
  clipId: string;
  search: string;
  videoDurationMs: number;
  enabled: boolean;
}) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const [isFetching, setIsFetching] = useState(false);

  const key = useMemo(() => getSearchPrivateTranscriptKey(clipId, search), [clipId, search]);

  const { fetchQueue } = useSearchTranscriptFetchQueueContext();

  useEffect(() => {
    fetchQueue.current?.cancelAll();
  }, [clipId, fetchQueue, search]);

  const searchTranscriptSegments = useCallback(
    async ({ startTimeMs, endTimeMs }: TimeRange) => {
      invariant(currentWorkspace, 'Current workspace should be defined');

      try {
        const searchResults = await Clips.searchTranscriptSegments({
          clipId,
          search,
          workspaceId: currentWorkspace?.id,
          startTimeMs: Math.round(startTimeMs),
          endTimeMs: Math.round(endTimeMs),
        });

        return {
          data: searchResults.data.matchingSegments,
          previousEndTimeMs: searchResults.pagination.previousEndTimeMs ?? startTimeMs,
          nextStartTimeMs: searchResults.pagination.nextStartTimeMs ?? videoDurationMs,
          metadata: { total: searchResults.data.total },
        };
      } catch (error) {
        const typedError = convertUnknownToError(error);
        reportErrorToBugsnag({
          error: typedError,
          context: 'Failed to search transcript segments',
          metadata: { clipId, startTimeMs, endTimeMs, search },
        });
        throw typedError;
      }
    },
    [clipId, currentWorkspace, search, videoDurationMs],
  );

  const fetchQueryData = useCallback(
    async ({ startTimeMs, endTimeMs }: TimeRange): Promise<SearchTranscriptCache> => {
      const queryData = queryClient.getQueryData<SearchTranscriptCache>(key);

      const newData = await getDataForTimeInterval({
        startTimeMs,
        endTimeMs,
        initialData: queryData?.data ?? [],
        fetchData: searchTranscriptSegments,
      });

      const isAllLoaded =
        newData.length === 1 && newData[0].startTimeMs === 0 && newData[0].endTimeMs >= videoDurationMs;

      return {
        data: newData,
        hasMore: !isAllLoaded,
        total: newData[0].metadata?.total ?? 0,
      };
    },
    [key, queryClient, searchTranscriptSegments, videoDurationMs],
  );

  const _searchTranscriptInRange = useCallback(
    async ({ startTimeMs, endTimeMs }: TimeRange) => {
      setIsFetching(true);
      try {
        const newData = await fetchQueryData({ startTimeMs, endTimeMs });
        queryClient.setQueryData(key, newData);
        return newData;
      } finally {
        setIsFetching(false);
      }
    },
    [fetchQueryData, key, queryClient],
  );

  const searchTranscriptInRange = useCallback(
    (timeRange: TimeRange) =>
      fetchQueue.current?.run(`${timeRange.startTimeMs}-${timeRange.endTimeMs}`, () =>
        _searchTranscriptInRange(timeRange),
      ),
    [_searchTranscriptInRange, fetchQueue],
  );

  const { data } = useQuery({
    queryKey: key,
    queryFn: () => searchTranscriptInRange({ startTimeMs: 0, endTimeMs: MAX_TRANSCRIPT_RANGE_IN_MS }),
    staleTime: ONE_DAY,
    enabled: enabled && !!currentWorkspace?.id && !!clipId && search.length >= MIN_TRANSCRIPT_QUERY_LENGTH,
  });

  return {
    data,
    isFetching,
    searchTranscriptInRange,
    key,
  };
};
